"use client";
// Client Component: controlled inputs (useState) and calls `signIn` from
// next-auth/react, which is a client-only API.

import { useState, type FormEvent } from "react";
import { signIn } from "next-auth/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function CredentialsLoginForm({
  callbackUrl = "/",
}: {
  /** Same-origin path to land on after signing in (see `safeCallbackPath`). */
  callbackUrl?: string;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email || !password) return;
    setIsSubmitting(true);
    setError(null);
    const result = await signIn("credentials", {
      email,
      password,
      redirect: false,
      callbackUrl,
    });
    setIsSubmitting(false);
    if (!result) {
      setError("Sign in failed. Please retry.");
      return;
    }
    if (result.error) {
      setError("Invalid email or password.");
      return;
    }
    // Use a relative path instead of `result.url` from Auth.js — when
    // the deployment lives behind a proxy, the URL Auth.js bakes
    // sometimes resolves to the upstream's own address (e.g.
    // `http://localhost:3000`) instead of the host the user typed,
    // and the browser then follows that to its own localhost. A
    // relative redirect is always evaluated against the current page
    // origin, so it stays on the host the user logged in from.
    window.location.assign(callbackUrl);
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={onSubmit}>
      <div className="flex flex-col gap-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={isSubmitting}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={isSubmitting}
        />
      </div>
      {error ? (
        <p role="alert" className="text-destructive text-sm">
          {error}
        </p>
      ) : null}
      <Button type="submit" disabled={isSubmitting || !email || !password}>
        {isSubmitting ? "Signing in..." : "Sign in"}
      </Button>
    </form>
  );
}
